import { Link, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBackground,
  BrandMark,
  LanguageToggle,
  OfflineBanner,
  ThemeToggle,
  Typewriter,
} from '@shared/components';

/**
 * Sign-in shell.
 * - `/login` (showcase): a full-bleed split — the showcase image runs edge to
 *   edge on one side with the brand overlaid on it; the form sits on the other
 *   side with the language/theme toggles in their own top row. No divider bar
 *   above the image, and it mirrors under `dir="rtl"` (the flex row flips).
 *   Legible in both themes: brand is white over the always-dark image; the
 *   toggles live on the themed form pane.
 * - admin login: a plain centered card under a bordered header (unchanged).
 */
export function AuthLayout(): React.JSX.Element {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  if (pathname === '/login') {
    return (
      <div className="relative flex min-h-screen flex-col">
        {/* Same ambient surface used across the signed-in app. */}
        <AppBackground />
        <OfflineBanner />
        <div className="flex flex-1">
          {/* Showcase image — full-bleed to the top, brand overlaid on it. */}
          <aside className="relative hidden w-1/2 overflow-hidden lg:block">
            <img
              src="/img/login.png"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover object-[30%_center]"
            />
            <div className="absolute inset-0 [background:linear-gradient(to_top,rgba(4,6,9,0.92),rgba(4,6,9,0.35)_45%,rgba(4,6,9,0.2))]" />
            <Link
              to="/"
              className="absolute inset-x-0 top-0 flex items-center gap-2.5 p-8 text-white"
            >
              <BrandMark size={26} />
              <span className="text-base font-semibold tracking-tight">{t('nav.brand')}</span>
            </Link>
            <div className="absolute inset-x-0 bottom-0 p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70">
                {t('landing.eyebrow')}
              </p>
              <Typewriter
                text={t('landing.title')}
                className="mt-3 inline-block min-h-[2.8em] max-w-md text-2xl font-semibold leading-snug text-white"
              />
            </div>
          </aside>

          {/* Form side — toggles top, form centred. Brand appears here only when
              the image is hidden (below lg). */}
          <div className="flex flex-1 flex-col">
            <div className="flex items-center gap-3 p-4 sm:px-6">
              <Link to="/" className="flex items-center gap-2.5 lg:hidden">
                <BrandMark size={26} />
                <span className="text-base font-semibold tracking-tight">{t('nav.brand')}</span>
              </Link>
              <div className="ms-auto flex items-center gap-2">
                <LanguageToggle />
                <ThemeToggle />
              </div>
            </div>
            <div className="flex flex-1 items-center justify-center px-4 pb-12">
              <div className="w-full max-w-sm">
                <Outlet />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin login — plain centered card under a bordered header.
  return (
    <div className="flex min-h-screen flex-col bg-bg">
      <OfflineBanner />
      <header className="border-b border-line">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-3 px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandMark size={26} />
            <span className="hidden text-base font-semibold tracking-tight sm:inline">
              {t('nav.brand')}
            </span>
          </Link>
          <div className="ms-auto flex items-center gap-2">
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-sm">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
